/**
 * Design tokens — single source of truth for CRM theming.
 *
 * These two token sets (light + dark) feed BOTH:
 *   1. CSS custom properties (see cssVars.js) consumed by theme.css / component CSS
 *   2. the MUI theme (see muiTheme.js) via createTheme
 *
 * Token group + key names are chosen so the generated CSS var is `--{group}-{key}`
 * and matches the legacy variable names theme.css consumers already reference
 * (e.g. group `bg` + key `primary` => `--bg-primary`).
 *
 * WCAG: every accent/status color has an EXPLICIT dark variant (not the light hex),
 * tuned for >= 4.5:1 (AA) contrast on the dark surfaces. Status colors additionally
 * expose a full badge triplet {fg, bg, border} per mode — verified AA in both modes
 * (see docs/audits + wcag check). Do NOT eyeball-derive dark values; edit here.
 */

// --- Semantic status badge families ---------------------------------------
// Each family is a {fg, bg, border} triplet per mode. Statuses alias to a family
// so overlapping states (won/approved/success = green) stay visually consistent.
const FAMILIES = {
  green: {
    light: { fg: '#166534', bg: '#dcfce7', border: '#bbf7d0' },
    dark: { fg: '#4ade80', bg: '#14311f', border: '#22c55e' }
  },
  red: {
    light: { fg: '#991b1b', bg: '#fee2e2', border: '#fecaca' },
    dark: { fg: '#f87171', bg: '#341b1b', border: '#b91c1c' }
  },
  amber: {
    light: { fg: '#92400e', bg: '#fef3c7', border: '#fde68a' },
    dark: { fg: '#fbbf24', bg: '#35270a', border: '#b45309' }
  },
  blue: {
    light: { fg: '#1e40af', bg: '#dbeafe', border: '#bfdbfe' },
    dark: { fg: '#60a5fa', bg: '#14213f', border: '#2563eb' }
  },
  violet: {
    light: { fg: '#6b21a8', bg: '#f3e8ff', border: '#e9d5ff' },
    dark: { fg: '#a78bfa', bg: '#2a1a40', border: '#7c3aed' }
  },
  slate: {
    light: { fg: '#374151', bg: '#f3f4f6', border: '#e5e7eb' },
    dark: { fg: '#9ca3af', bg: '#1f2937', border: '#374151' }
  },
  orange: {
    light: { fg: '#9a3412', bg: '#ffedd5', border: '#fed7aa' },
    dark: { fg: '#fdba74', bg: '#3a1f0f', border: '#c2410c' }
  },
  fuchsia: {
    light: { fg: '#86198f', bg: '#fae8ff', border: '#f5d0fe' },
    dark: { fg: '#e879f9', bg: '#331033', border: '#a21caf' }
  },
  warmgray: {
    light: { fg: '#57534e', bg: '#f5f5f4', border: '#e7e5e4' },
    dark: { fg: '#d6d3d1', bg: '#292524', border: '#57534e' }
  }
};

// Status -> family. `negotiating` folds the old terracotta #c8614a into orange.
const STATUS_FAMILY = {
  draft: 'slate',
  sent: 'blue',
  viewed: 'violet',
  pending: 'amber',
  approved: 'green',
  negotiating: 'orange',
  counter: 'fuchsia',
  won: 'green',
  lost: 'red',
  expired: 'warmgray',
  rejected: 'red',
  success: 'green',
  warning: 'amber',
  error: 'red',
  info: 'blue'
};

function buildStatus(mode) {
  const out = {};
  for (const [status, family] of Object.entries(STATUS_FAMILY)) {
    const t = FAMILIES[family][mode];
    out[status] = t.fg; // --status-{name}        (foreground / icon / dot)
    out[`${status}-bg`] = t.bg; // --status-{name}-bg     (chip background)
    out[`${status}-border`] = t.border; // --status-{name}-border
    // --status-{name}-solid: MODE-INVARIANT saturated fill for interactive
    // buttons, dark enough that white text passes AA in BOTH modes (verified).
    out[`${status}-solid`] = FAMILIES[family].light.fg;
  }
  return out;
}

// Brand gradient — brand identity, mode-invariant (does NOT theme-shift).
const brand = {
  'gradient-start': '#667eea',
  'gradient-end': '#764ba2'
};

export const lightTokens = {
  bg: {
    primary: '#ffffff',
    secondary: '#f9fafb',
    tertiary: '#f3f4f6',
    hover: '#e5e7eb',
    elevated: '#ffffff'
  },
  text: {
    primary: '#111827',
    secondary: '#374151',
    tertiary: '#6b7280',
    muted: '#9ca3af',
    disabled: '#9ca3af',
    'on-accent': '#ffffff'
  },
  border: {
    color: '#e5e7eb',
    light: '#f3f4f6'
  },
  accent: {
    primary: '#6366f1',
    'primary-hover': '#4f46e5',
    success: '#16a34a',
    'success-hover': '#15803d',
    warning: '#b45309',
    danger: '#dc2626',
    info: '#2563eb',
    // O-dark: accent-hued TEXT (a current entry, a link) on the page's surfaces. --accent-primary
    // reads 4.47:1 on white; this reads 6.29:1 on --bg-elevated, 5.08:1 on --bg-hover. Opt-in:
    // nothing inherits it.
    text: '#4f46e5'
  },
  // A filled accent button's pair (customers polish 2): --text-on-accent on --accent-primary
  // reads 4.47:1 here and 2.98:1 in dark. fg on bg 6.29:1, on hover 7.90:1.
  // Polish 4 (ConfirmDialog): a danger pair (fg on bg 6.47:1, on hover 8.31:1) and a warning
  // pair (5.02:1, 7.09:1), the same shape.
  btn: {
    'accent-bg': '#4f46e5',
    'accent-bg-hover': '#4338ca',
    'accent-fg': '#ffffff',
    'danger-bg': '#b91c1c',
    'danger-bg-hover': '#991b1b',
    'danger-fg': '#ffffff',
    'warning-bg': '#b45309',
    'warning-bg-hover': '#92400e',
    'warning-fg': '#ffffff'
  },
  status: buildStatus('light'),
  brand,
  card: {
    bg: '#ffffff',
    'hover-bg': '#fafafa'
  },
  input: {
    bg: '#ffffff',
    border: '#d1d5db',
    'focus-border': '#6366f1',
    text: '#111827',
    placeholder: '#9ca3af'
  },
  sidebar: {
    bg: '#1e1e2f',
    text: '#ffffff',
    hover: 'rgba(255, 255, 255, 0.1)',
    active: 'rgba(99, 102, 241, 0.2)'
  },
  overlay: {
    bg: 'rgba(0, 0, 0, 0.5)'
  },
  table: {
    'header-bg': '#f9fafb',
    'row-hover': '#f3f4f6',
    border: '#e5e7eb'
  },
  scrollbar: {
    track: '#f3f4f6',
    thumb: '#d1d5db',
    'thumb-hover': '#9ca3af'
  },
  shadow: {
    sm: '0 1px 2px rgba(0, 0, 0, 0.05)',
    md: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
    lg: '0 10px 15px -3px rgba(0, 0, 0, 0.1)'
  }
};

export const darkTokens = {
  bg: {
    primary: '#111827',
    secondary: '#1f2937',
    tertiary: '#374151',
    hover: '#4b5563',
    elevated: '#1f2937'
  },
  text: {
    primary: '#f9fafb',
    secondary: '#e5e7eb',
    tertiary: '#9ca3af',
    muted: '#6b7280',
    disabled: '#6b7280',
    'on-accent': '#ffffff'
  },
  border: {
    color: '#374151',
    light: '#4b5563'
  },
  accent: {
    primary: '#818cf8',
    'primary-hover': '#a5b4fc',
    success: '#4ade80',
    'success-hover': '#22c55e',
    warning: '#fbbf24',
    danger: '#f87171',
    info: '#60a5fa',
    // 7.36:1 on --bg-elevated, 8.90:1 on --bg-primary (not for a --bg-hover row: 3.79:1).
    text: '#a5b4fc'
  },
  // Dark text on the light indigo: fg on bg 5.95:1, on hover 8.90:1. Polish 4: dark text on
  // the light red (6.41:1, on hover 9.35:1) and on the amber (10.63:1, 12.30:1).
  btn: {
    'accent-bg': '#818cf8',
    'accent-bg-hover': '#a5b4fc',
    'accent-fg': '#111827',
    'danger-bg': '#f87171',
    'danger-bg-hover': '#fca5a5',
    'danger-fg': '#111827',
    'warning-bg': '#fbbf24',
    'warning-bg-hover': '#fcd34d',
    'warning-fg': '#111827'
  },
  status: buildStatus('dark'),
  brand,
  card: {
    bg: '#1f2937',
    'hover-bg': '#374151'
  },
  input: {
    bg: '#1f2937',
    border: '#4b5563',
    'focus-border': '#818cf8',
    text: '#f9fafb',
    placeholder: '#6b7280'
  },
  sidebar: {
    bg: '#0f0f1a',
    text: '#ffffff',
    hover: 'rgba(255, 255, 255, 0.1)',
    active: 'rgba(129, 140, 248, 0.28)'
  },
  overlay: {
    bg: 'rgba(0, 0, 0, 0.7)'
  },
  table: {
    'header-bg': '#1f2937',
    'row-hover': '#374151',
    border: '#374151'
  },
  scrollbar: {
    track: '#1f2937',
    thumb: '#4b5563',
    'thumb-hover': '#6b7280'
  },
  shadow: {
    sm: '0 1px 2px rgba(0, 0, 0, 0.3)',
    md: '0 4px 6px -1px rgba(0, 0, 0, 0.4)',
    lg: '0 10px 15px -3px rgba(0, 0, 0, 0.5)'
  }
};

export const tokens = { light: lightTokens, dark: darkTokens };

export default tokens;
